import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import Guid from "@/app/types/common/guid";
import { MenuItem } from "@/app/ui/navigation/types";
import type { Location } from "vue-router";
import { hasPermissionsUsingUserStore } from "@/components/pages/administration/user_roles/UserAccessHandler";
import { EnumSalesVisitAssociatedEntityType } from "@/app/services/generated/enums/enumSalesVisitAssociatedEntityType.generated";
import { LocalTime, LocalDate } from "@js-joda/core";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";
import { keys } from "@/plugins/i18n";

const builder = new RouteBuilder("Sales_");

export const salesRoutes = {
  createOrder: builder.routeWithQuery(
    {
      url: "/orders/create-order",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/orders/CreateOrderPage.vue"
        ),
      componentProps: (route) => ({
        outletId: Guid.tryParse(route.query?.outletId?.toString()),
        salesVisitLogId: Guid.tryParse(
          route.query?.salesVisitLogId?.toString()
        ),
        returnCrmOutletId: Guid.tryParse(
          route.query?.returnCrmOutletId?.toString()
        ),
      }),
      requiredUserPermissions: [EnumUserPermission.ordersEdit],
    },
    (opts: {
      outletId?: Guid | null;
      salesVisitLogId?: Guid;
      returnCrmOutletId?: Guid;
    }) => ({
      outletId: opts.outletId?.toString(),
      salesVisitLogId: opts.salesVisitLogId?.toString(),
      returnCrmOutletId: opts.returnCrmOutletId?.toString(),
    })
  ),
  viewOrder: builder.routeWithParam(
    {
      url: "/orders/view-order/:id",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/orders/ViewOrderPage.vue"
        ),
      componentProps: (route) => ({
        id: Guid.tryParse(route.params.id),
        salesVisitLogId: Guid.tryParse(route.params.salesVisitLogId),
        returnCrmOutletId: Guid.tryParse(route.params.returnCrmOutletId),
      }),
      requiredUserPermissions: [EnumUserPermission.ordersView],
    },
    (opts: {
      id: Guid;
      salesVisitLogId?: Guid | null;
      returnCrmOutletId?: Guid | null;
    }) => ({
      id: opts.id.toString(),
      salesVisitLogId: opts.salesVisitLogId?.toString(),
      returnCrmOutletId: opts.returnCrmOutletId?.toString(),
    })
  ),
  editOrder: builder.routeWithParam(
    {
      url: "/orders/edit-order/:id",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/orders/EditOrderPage.vue"
        ),
      componentProps: (route) => ({
        id: Guid.tryParse(route.params.id),
        salesVisitLogId: Guid.tryParse(route.params.salesVisitLogId),
        returnCrmOutletId: Guid.tryParse(route.params.returnCrmOutletId),
      }),
      requiredUserPermissions: [EnumUserPermission.ordersEdit],
    },
    (opts: {
      id: Guid;
      salesVisitLogId?: Guid | null;
      returnCrmOutletId?: Guid | null;
    }) => ({
      id: opts.id.toString(),
      salesVisitLogId: opts.salesVisitLogId?.toString(),
      returnCrmOutletId: opts.returnCrmOutletId?.toString(),
    })
  ),
  bulkPlanSalesVisits: builder.route({
    url: "/sales/sales-visits/bulk-create",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "sales-visit" */ "@/pages/sales/salesvisits/BulkPlanSalesVisitsPage.vue"
      ),
  }),
  optimiseSalesVisits: builder.route({
    url: "/sales/sales-visits/optimise-visits",
    view: () =>
      import(
        /* webpackChunkName: "sales-visit" */ "@/pages/sales/salesvisits/OptimiseVisitsPage.vue"
      ),
  }),
  visitPlanningMap: builder.route({
    url: "/sales/sales-visits/planning-map",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "sales-visit" */ "@/pages/sales/salesvisits/VisitPlanningMapPage.vue"
      ),
  }),

  salesQuestionsList: builder.routeWithQuery(
    {
      url: "/sales/sales-visits/sales-question-list",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/salesvisits/SalesQuestionListPage.vue"
        ),
      componentProps: (route) => ({
        type: EnumSalesVisitAssociatedEntityType.getValues().find(
          (x) => x.value === route.query?.type
        ),
      }),
    },
    (opts: { type?: EnumSalesVisitAssociatedEntityType }) => ({
      type: opts.type?.value,
    })
  ),
  salesQuestion: builder.routeWithQuery(
    {
      url: "/sales/sales-visits/sales-question",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "orders" */ "@/pages/sales/salesvisits/SalesQuestionPage.vue"
        ),
      componentProps: (route) => ({
        id: Guid.parseOrEmpty(route.query?.id?.toString()),
        mode: route.query?.mode ?? "view",
        fromType: EnumSalesVisitAssociatedEntityType.getValues().find(
          (x) => x.value === route.query?.fromType
        ),
      }),
    },
    (opts: {
      id: Guid;
      mode: "create" | "view" | "edit";
      fromType?: EnumSalesVisitAssociatedEntityType;
    }) => ({
      id: opts.id.toString(),
      mode: opts.mode,
      fromType: opts.fromType?.value,
    })
  ),
};

const menuNames = {
  orders: {
    list: "orders_list",
    edit: "edit_order",
  },
};

export const salesMenu = {
  root: "navigation_sales_group",
  listOrders: () => ({
    name: menuNames.orders.list,
  }),
  viewOrder: (id: Guid) => salesRoutes.viewOrder.location({ id }),
  creditNotes: {
    list: "credit_notes_list",
    edit: "edit_credit_note",
    create: "create_credit_note",
    view: "view_credit_note",
  },
  createCreditNote: (opts?: {
    outletId?: Guid | null;
    ullageId?: Guid | null;
    despatchDate?: LocalDate | null;
  }): Location => ({
    name: salesMenu.creditNotes.create,
    params: {
      outletId: opts?.outletId?.toString() ?? "",
      ullageId: opts?.ullageId?.toString() ?? "",
      despatchDate: opts?.despatchDate?.toString() ?? "",
    },
  }),
  viewCreditNote: (id: Guid): Location => ({
    name: salesMenu.creditNotes.view,
    params: { id: id.toString() },
  }),
  editCreditNote: (id: Guid): Location => ({
    name: salesMenu.creditNotes.edit,
    params: { id: id.toString() },
  }),
  listCreditNotes: (): Location => ({ name: salesMenu.creditNotes.list }),
  leads: {
    root: "leads_leads",
    pipeline: "leads_pipeline",
    list: "leads_list",
    edit: "leads_edit",
    create: "leads_create",
    finder: "leads_finder",
    viewLeads: (): Location => ({
      name: salesMenu.leads.list,
    }),
    createLead: () => ({
      name: salesMenu.leads.create,
    }),
    createLeadFromLeadsMap: (opts: {
      businessName: string;
      telephoneNumber: string;
      addressLine1: string;
      addressLine2: string;
      addressLine3: string;
      addressLine4: string;
      addressLine5: string;
      addressLine6: string;
      addressPostcode: string;
      webAddress: string;
    }): Location => ({
      name: salesMenu.leads.create,
      query: opts,
    }),
    viewLead: (leadId: Guid): Location => ({
      name: salesMenu.leads.edit,
      params: {
        id: leadId.toString(),
      },
    }),
  },
  salesVisits: {
    root: "navigation_sales_visits_group",
    planned: {
      list: "planned_sales_visit_list",
      listLocation: (): Location => ({
        name: salesMenu.salesVisits.planned.list,
      }),
      edit: "planned_sales_visit_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: salesMenu.salesVisits.planned.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (): Location => ({
        name: salesMenu.salesVisits.planned.edit,
      }),
      duplicateLocation: (id: Guid): Location => ({
        name: salesMenu.salesVisits.planned.edit,
        params: {
          duplicateFromId: id.toString(),
        },
      }),
    },
    logs: {
      list: "sales_visit_log_list",
      listLocation: (): Location => ({
        name: salesMenu.salesVisits.logs.list,
      }),
      edit: "sales_visit_log_edit",
      editLocation: (id: Guid, isEditing: boolean): Location => ({
        name: salesMenu.salesVisits.logs.edit,
        params: {
          id: id.toString(),
          mode: isEditing ? "edit" : "view",
        },
      }),
      createLocation: (opts?: {
        entityType: EnumSalesVisitAssociatedEntityType;
        entityId?: Guid | null;
        date?: LocalDate;
        time?: LocalTime;
        userId?: Guid;
        adHocBusinessName?: string;
      }): Location => ({
        name: salesMenu.salesVisits.logs.edit,
        query: {
          entityType: opts?.entityType.key.toString(),
          entityId: opts?.entityId?.toString(),
          date: opts?.date?.toString(),
          time: opts?.time?.toString(),
          userId: opts?.userId?.toString(),
          adHocBusinessName: opts?.adHocBusinessName,
        },
      }),
    },
    visitPlan: "sales_visit_plan",
  },
};

export const buildSalesMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    name: salesMenu.root,
    icon: "ShoppingBasket",
    visible: () =>
      hasPermissionsUsingUserStore([EnumUserPermission.menuSalesView]),
    subMenus: [
      MenuItem.fromOpts({
        name: menuNames.orders.list,
        icon: "List",
        url: "/orders/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/ListOrders.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.ordersView],
      }),
      MenuItem.forRoute("", salesRoutes.createOrder, { visible: false }),
      MenuItem.forRoute("", salesRoutes.viewOrder, { visible: false }),
      MenuItem.forRoute("", salesRoutes.editOrder, { visible: false }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.list,
        icon: "List",
        url: "/creditnotes/list",
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/components/pages/orders/creditnotes/ListCreditNotes.vue"
          ),
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.create,
        url: "/creditnotes/create-credit-note",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/CreateCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          outletId: Guid.tryParse(route.params.outletId),
          ullageId: Guid.tryParse(route.params.ullageId),
          despatchDate: route.params.despatchDate
            ? LocalDate.parse(route.params.despatchDate)
            : null,
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesEdit],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.edit,
        url: "/creditnotes/edit-credit-note/:id",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/EditCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesEdit],
      }),
      MenuItem.fromOpts({
        name: salesMenu.creditNotes.view,
        url: "/creditnotes/view-credit-note/:id",
        breadcrumbParentName: salesMenu.creditNotes.list,
        view: (): Promise<any> =>
          import(
            /* webpackChunkName: "orders" */ "@/pages/sales/creditnotes/ViewCreditNotePage.vue"
          ),
        componentProps: (route) => ({
          id: Guid.tryParse(route.params.id),
        }),
        visible: false,
        requiredUserPermissions: [EnumUserPermission.creditNotesView],
      }),
      MenuItem.forBranch({
        name: salesMenu.leads.root,
        subMenus: [
          MenuItem.fromOpts({
            name: salesMenu.leads.list,
            url: "/leads/list",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/ListLeads.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.pipeline,
            url: "/leads/pipeline",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/LeadsPipeline.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.finder,
            url: "/leads/map",
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/LeadsFinder.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.create,
            url: "/leads/create",
            breadcrumbParentName: salesMenu.leads.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/EditLead.vue"
              ),
            visible: false,
          }),
          MenuItem.fromOpts({
            name: salesMenu.leads.edit,
            url: "/leads/view/:id",
            breadcrumbParentName: salesMenu.leads.list,
            view: (): Promise<any> =>
              import(
                /* webpackChunkName: "orders" */ "@/components/pages/orders/leads/EditLead.vue"
              ),
            visible: false,
            componentProps: (route) => ({
              id: route.params.id,
            }),
          }),
        ],
      }),

      MenuItem.forBranch({
        name: salesMenu.salesVisits.root,
        subMenus: [
          MenuItem.forRoute(
            keys.sales_visit_planning_map,
            salesRoutes.visitPlanningMap
          ),
          MenuItem.forRoute(
            keys.sales_visit_optimise_sales_visits,
            salesRoutes.optimiseSalesVisits
          ),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.planned.list,
            url: "/sales/sales-visits/planned-visits",
            breadcrumbParentName: salesMenu.salesVisits.root,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/ListPlannedSalesVisits.vue"
              ),
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.logs.list,
            url: "/sales/sales-visits/logs",
            breadcrumbParentName: salesMenu.salesVisits.root,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/ListSalesVisitLogs.vue"
              ),
          }),
          MenuItem.forRoute(
            keys.enum_noun_sales_question_plural,
            salesRoutes.salesQuestionsList
          ),

          MenuItem.fromOpts({
            name: salesMenu.salesVisits.planned.edit,
            url: "/sales/sales-visits/planned-visit/:id?/:mode?",
            breadcrumbParentName: salesMenu.salesVisits.planned.list,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/EditPlannedSalesVisit.vue"
              ),
            visible: false,
            componentProps: (route) => ({
              id: Guid.tryParse(route.params.id),
              edit: route.params.mode === "edit",
              duplicateFromId: Guid.tryParse(route.params.duplicateFromId),
            }),
          }),
          MenuItem.fromOpts({
            name: salesMenu.salesVisits.logs.edit,
            url: "/sales/sales-visits/log/:id?/:mode?",
            breadcrumbParentName: salesMenu.salesVisits.logs.list,
            view: () =>
              import(
                /* webpackChunkName: "sales-visit" */ "@/components/pages/orders/salesvisits/EditSalesVisitLog.vue"
              ),
            visible: false,
            componentProps: (route) => ({
              id: Guid.tryParse(route.params.id),
              edit: route.params.mode === "edit",
              entityType: EnumSalesVisitAssociatedEntityType.getValues().find(
                (x) => x.key.toString() === route.query?.entityType
              ),
              entityId: Guid.tryParse(route.query?.entityId?.toString()),
              date: route.query?.date
                ? LocalDate.parse(route.query?.date?.toString())
                : null,
              time: route.query?.time
                ? LocalTime.parse(route.query?.time?.toString())
                : null,
              userId: Guid.tryParse(route.query?.userId?.toString()),
              adHocBusinessName: route.query?.adHocBusinessName?.toString(),
            }),
          }),
          MenuItem.forRoute("BulkPlan", salesRoutes.bulkPlanSalesVisits, {
            visible: false,
          }),
          MenuItem.forRoute("SalesQuestion", salesRoutes.salesQuestion, {
            visible: false,
          }),
        ],
      }),
    ],
  }),
];
